import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import {
  computeAdherence,
  type AdministrationRecord,
  type MedicationConcept,
} from '@toxofollow/shared';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Badge, Table, inputClass } from '../components/ui.js';
import { QueryState } from '../components/form.js';

interface AdministrationApiRow {
  id: string;
  orderId: string;
  scheduledDate: string;
  scheduledTime: string | null;
  status: string;
  actualTime: string | null;
}
interface OrderRow {
  id: string;
  concept: MedicationConcept;
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function pctTone(pct: number): 'ok' | 'warn' | 'danger' {
  if (pct >= 90) return 'ok';
  if (pct >= 75) return 'warn';
  return 'danger';
}

export function Adherence() {
  const { t } = useI18n();
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));

  const ordersQ = useQuery({ queryKey: ['medications', 'orders'], queryFn: () => api.get<{ orders: OrderRow[] }>('/medications/orders') });
  const adminsQ = useQuery({
    queryKey: ['administrations', from, to],
    queryFn: () => api.get<{ administrations: AdministrationApiRow[] }>(`/medications/administrations?from=${from}&to=${to}`),
  });

  const stats = useMemo(() => {
    const orders = ordersQ.data?.orders ?? [];
    const conceptByOrder = new Map(orders.map((o) => [o.id, o.concept]));
    const records: AdministrationRecord[] = (adminsQ.data?.administrations ?? []).map((a) => ({
      orderId: a.orderId,
      concept: conceptByOrder.get(a.orderId) ?? 'custom',
      scheduledDate: a.scheduledDate,
      scheduledTime: a.scheduledTime ?? '00:00',
      status: a.status,
      actualTime: a.actualTime,
    }));
    return computeAdherence(records);
  }, [adminsQ.data, ordersQ.data]);

  const barData = Object.values(stats.byConcept).map((c) => ({
    name: t(`concept.${c.concept}`),
    pct: c.adherencePct,
  }));

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">{t('adherence.title')}</h1>

      <Card title={t('adherence.range')}>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">{t('common.from')}</span>
            <input className={inputClass} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">{t('common.to')}</span>
            <input className={inputClass} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      </Card>

      <QueryState
        isLoading={adminsQ.isLoading || ordersQ.isLoading}
        isError={adminsQ.isError || ordersQ.isError}
        error={adminsQ.error ?? ordersQ.error}
        errorFallback={t('common.error')}
        isEmpty={stats.totalScheduled === 0}
        emptyMessage="—"
      >
        <Card title="">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-4xl font-bold text-slate-800">{stats.adherencePct}%</span>
            <Badge tone={pctTone(stats.adherencePct)}>{stats.taken + stats.late + stats.partial}/{stats.totalScheduled}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat label={t('admin.taken')} value={stats.taken} tone="ok" />
            <Stat label={t('admin.late')} value={stats.late} tone="warn" />
            <Stat label={t('admin.missed')} value={stats.missed} tone="danger" />
            <Stat label={t('admin.refused')} value={stats.refused} tone="danger" />
            <Stat label={t('admin.vomited_after_dose')} value={stats.vomited} tone="danger" />
            <Stat label={t('admin.partial')} value={stats.partial} tone="info" />
          </div>
        </Card>

        <Card title={t('adherence.perMedication')}>
          {barData.length > 0 && (
            <ResponsiveContainer width="100%" height={Math.max(160, barData.length * 48)}>
              <BarChart data={barData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => [`${v}%`, '']} contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="pct" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                  {barData.map((d, i) => (
                    <Cell key={i} fill={d.pct >= 90 ? '#16a34a' : d.pct >= 75 ? '#f59e0b' : '#dc2626'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <Table headers={[t('concept.custom'), '%', t('admin.taken'), t('admin.late'), t('admin.missed')]}>
            {Object.values(stats.byConcept).map((c) => (
              <tr key={c.concept} className="border-b border-slate-100">
                <td className="py-2 pr-4">{t(`concept.${c.concept}`)}</td>
                <td className="py-2 pr-4"><Badge tone={pctTone(c.adherencePct)}>{c.adherencePct}%</Badge></td>
                <td className="py-2 pr-4">{c.taken}</td>
                <td className="py-2 pr-4">{c.late}</td>
                <td className="py-2 pr-4">{c.missed}</td>
              </tr>
            ))}
          </Table>
        </Card>
      </QueryState>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'danger' | 'info' }) {
  const dot: Record<string, string> = {
    ok: 'bg-green-500',
    warn: 'bg-amber-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500',
  };
  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <span className={`inline-block w-2 h-2 rounded-full ${dot[tone]}`} />
        {label}
      </div>
      <div className="text-xl font-semibold text-slate-800">{value}</div>
    </div>
  );
}
